import { RealEstateCategory, SegmentationType, TransactionType } from '../../../@types/types'

export enum IntentType {
  NO_INFORMATION = 'NO_INFORMATION',
  TENANT = 'TENANT',
  BUYER = 'BUYER',
  NEXT_TENANT = 'NEXT_TENANT',
}

export const segmentationTypeArray = Object.keys(SegmentationType).map(
  (key) => SegmentationType[key as keyof typeof SegmentationType]
)

const businessChildren = [
  RealEstateCategory.SPECIAL_INDUSTRY,
  RealEstateCategory.OFFICE,
  RealEstateCategory.GASTRONOMY,
  RealEstateCategory.HANGAR,
  RealEstateCategory.STORE,
  RealEstateCategory.INDUSTRIAL_PLOT,
]

/**
 * NEW SEGMENTATION LOGIC
 * Decision tree for: Segmentation Type (Ich bin) → Transaction Type (Vorhaben) → Real Estate Type (Immobilientyp)
 */

/**
 * Decision tree: Defines which transaction types (Vorhaben) are available for each segmentation type (Ich bin)
 *
 * - TENANT: Can rent or short-term rent (RENT, SHORT_TERM_ACCOMMODATION)
 * - PROPERTY_OWNER: Can rent, sell, or short-term rent (RENT, SELL, SHORT_TERM_ACCOMMODATION)
 * - AGENT: Can rent, sell, or short-term rent (RENT, SELL, SHORT_TERM_ACCOMMODATION)
 *
 * Note: SHORT_TERM_ACCOMMODATION has no children (goes directly to endpoint)
 */
const SEGMENTATION_TRANSACTION_AVAILABILITY: Record<
  SegmentationType,
  (TransactionType | 'SHORT_TERM_ACCOMMODATION')[]
> = {
  [SegmentationType.PROPERTY_OWNER]: [TransactionType.RENT, TransactionType.SELL, 'SHORT_TERM_ACCOMMODATION'],
  [SegmentationType.TENANT]: [TransactionType.RENT, 'SHORT_TERM_ACCOMMODATION'],
  [SegmentationType.AGENT]: [TransactionType.RENT, TransactionType.SELL, 'SHORT_TERM_ACCOMMODATION'],
}

/**
 * Decision tree: Defines which real estate categories (Immobilientyp) are available
 * for each segmentation + transaction combination
 *
 * Note: SHORT_TERM_ACCOMMODATION has empty array [] because it has no children
 */
const SEGMENTATION_TRANSACTION_REAL_ESTATE_AVAILABILITY: Record<
  SegmentationType,
  Partial<Record<TransactionType | 'SHORT_TERM_ACCOMMODATION', (RealEstateCategory | 'BUSINESS')[]>>
> = {
  [SegmentationType.TENANT]: {
    [TransactionType.RENT]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.FLAT_SHARE_ROOM,
      RealEstateCategory.GARAGE,
      'BUSINESS',
    ],
    ['SHORT_TERM_ACCOMMODATION']: [],
  },
  [SegmentationType.PROPERTY_OWNER]: {
    [TransactionType.RENT]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.GARAGE,
      'BUSINESS',
    ],
    [TransactionType.SELL]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.PLOT,
      RealEstateCategory.INVESTMENT,
      RealEstateCategory.GARAGE,
      'BUSINESS',
    ],
    ['SHORT_TERM_ACCOMMODATION']: [],
  },
  [SegmentationType.AGENT]: {
    [TransactionType.RENT]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.FLAT_SHARE_ROOM,
      RealEstateCategory.GARAGE,
      'BUSINESS',
    ],
    [TransactionType.SELL]: [
      RealEstateCategory.APARTMENT,
      RealEstateCategory.HOUSE,
      RealEstateCategory.PLOT,
      RealEstateCategory.INVESTMENT,
      RealEstateCategory.GARAGE,
      'BUSINESS',
    ],
    ['SHORT_TERM_ACCOMMODATION']: [],
  },
}

/**
 * Returns the list of available transaction types (Vorhaben) for a given segmentation type (Ich bin)
 *
 * @param segmentationType - The selected segmentation type
 * @returns Array of available TransactionType values (including 'SHORT_TERM_ACCOMMODATION')
 */
export function getAvailableTransactions(
  segmentationType: SegmentationType
): (TransactionType | 'SHORT_TERM_ACCOMMODATION')[] {
  return SEGMENTATION_TRANSACTION_AVAILABILITY[segmentationType] || []
}

/**
 * Returns the list of available real estate categories (Immobilientyp) for a given
 * segmentation and transaction combination
 *
 * @param segmentationType - The selected segmentation type
 * @param transactionType - The selected transaction type (can be 'SHORT_TERM_ACCOMMODATION')
 * @returns Array of available RealEstateCategory values (including 'BUSINESS'). Returns empty array for SHORT_TERM_ACCOMMODATION
 */
export function getAvailableRealEstateTypes(
  segmentationType: SegmentationType,
  transactionType: TransactionType | 'SHORT_TERM_ACCOMMODATION'
): (RealEstateCategory | 'BUSINESS')[] {
  return SEGMENTATION_TRANSACTION_REAL_ESTATE_AVAILABILITY[segmentationType]?.[transactionType] || []
}

/**
 * Checks if a specific transaction type is available for a given segmentation type
 *
 * @param segmentationType - The segmentation type
 * @param transactionType - The transaction type to check
 * @returns true if the transaction is available, false otherwise
 */
export function isTransactionAvailable(segmentationType: SegmentationType, transactionType: TransactionType): boolean {
  return getAvailableTransactions(segmentationType).includes(transactionType)
}

/**
 * Returns business children (Gewerbetyp) when BUSINESS is selected
 * Filters out SPECIAL_INDUSTRY for specific segmentation + transaction combinations
 *
 * @param segmentationType - The selected segmentation type
 * @param transactionType - The selected transaction type
 * @returns Array of available business categories
 */
export function getBusinessChildren(
  segmentationType?: SegmentationType,
  transactionType?: TransactionType | 'SHORT_TERM_ACCOMMODATION'
): RealEstateCategory[] {
  // If no segmentation or transaction is provided, return all business children
  if (!segmentationType || !transactionType || transactionType === 'SHORT_TERM_ACCOMMODATION') {
    return businessChildren
  }

  // Filter out SPECIAL_INDUSTRY for all PROPERTY_OWNER, TENANT, and AGENT combinations
  // with RENT or SELL transactions (all Gewerbe combinations)
  const shouldHideSpecialIndustry =
    (segmentationType === SegmentationType.PROPERTY_OWNER &&
      (transactionType === TransactionType.RENT || transactionType === TransactionType.SELL)) ||
    (segmentationType === SegmentationType.TENANT && transactionType === TransactionType.RENT) ||
    (segmentationType === SegmentationType.AGENT &&
      (transactionType === TransactionType.RENT || transactionType === TransactionType.SELL))

  if (shouldHideSpecialIndustry) {
    return businessChildren.filter((category) => category !== RealEstateCategory.SPECIAL_INDUSTRY)
  }

  return businessChildren
}

/**
 * Checks if a transaction type has children (real estate types to select)
 *
 * @param segmentationType - The segmentation type
 * @param transactionType - The transaction type to check
 * @returns true if the transaction has real estate type children, false otherwise (e.g., SHORT_TERM_ACCOMMODATION)
 */
export function hasTransactionChildren(
  segmentationType: SegmentationType,
  transactionType: TransactionType | 'SHORT_TERM_ACCOMMODATION'
): boolean {
  const children = getAvailableRealEstateTypes(segmentationType, transactionType)
  return children.length > 0
}
